import React, { useState, useEffect } from 'react'
import { Card } from './ui/card'
import { Pill } from './ui/pill'
import { Input } from './ui/input'
import { 
  Search, 
  Filter, 
  Download, 
  Database, 
  HardDrive, 
  Trash2, 
  Calendar,
  Clock,
  FileText,
  Play,
  Square,
  Pause,
  FolderOpen,
  Settings,
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  ArrowUpDown,
  Copy,
  ExternalLink,
  Smartphone,
  Cloud
} from 'lucide-react'
import { cn } from './ui/utils'
import { toast } from 'sonner'
import { joinPath, formatPathForSystem, detectOS } from '../utils/pathUtils'
// Phase 4: 새로운 스토어 구조 사용
import { 
  useSystemStatus, 
  useDeviceStatus, 
  useStreamingStatus, 
  useRecordingStatus,
  useSystemActions 
} from '../stores/systemStore'
import { useSensorDataStore } from '../stores/sensorDataStore'
import { useConnectedDevice, useDeviceMonitoring, useSamplingRates } from '../stores/deviceStore'
import { bluetoothService } from '../utils/bluetoothService'
// 스트리밍 저장소 시스템 통합
import { useStorageStore, SessionInfo } from '../stores/storageStore'
import { ExportSessionModal } from './DataCenter/ExportSessionModal'
import { useSettingsStore } from '../stores/settingsStore'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Checkbox } from './ui/checkbox'
import { Alert, AlertDescription } from './ui/alert'
import { LoadingState } from './ui/LoadingState'
import { EmptyState } from './ui/EmptyState'
import { ErrorState } from './ui/ErrorState'
import { PageShell, PageHeader } from './ui/PageShell'
// 🔧 분석 지표 서비스 추가
import { AnalysisMetricsService } from '../services/AnalysisMetricsService'
// 🔧 처리된 데이터 스토어 추가
import { useProcessedDataStore } from '../stores/processedDataStore'
import { createLogger } from "../utils/logger";
import { useTranslation } from '../locales';
import { useLanguageStore } from '../stores/languageStore';

// NOTE: S3/StorageSetup 관련 state는 SDD 010 Phase 2에서 완전 제거 예정
// 현재는 빌드 통과를 위한 최소 스텁 유지

const logger = createLogger("DataCenter");
export function DataCenter() {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [saveOptions, setSaveOptions] = useState({
    eegRaw: true,
    ppgRaw: true,
    accRaw: true,
    // 🔧 processed 데이터 타입 제거 - 복잡성으로 인해 제외
    // eegProcessed: false,
    // ppgProcessed: false,
    // accProcessed: false,
    // 🔧 분석 지표 데이터 타입만 유지
    eegAnalysisMetrics: true,
    ppgAnalysisMetrics: true,
    accAnalysisMetrics: true
  })
  // 🔧 SDD 010 Phase 2에서 제거 예정 — 빌드 통과용 스텁
  const [showStorageSetup, setShowStorageSetup] = useState(false)
  const [selectedSessionForS3Download, setSelectedSessionForS3Download] = useState<any>(null)
  const [selectedStorageLocation, setSelectedStorageLocation] = useState<'local' | 's3'>('local')
  const [uploadingSessions, setUploadingSessions] = useState<Set<string>>(new Set())
  const [storageLocation, setStorageLocation] = useState<'s3' | 'local' | 'none'>('none')
  const [s3ConnectionFailed, setS3ConnectionFailed] = useState(false)
  const [isS3Disabled, setIsS3Disabled] = useState(false)
  const [restoreError, setRestoreError] = useState<string | null>(null)
  const isRestoring = false
  // StreamingSessionConfig 타입 스텁 (SDD 010)
  interface StreamingSessionConfig {
    sessionName: string;
    deviceName: string;
    deviceId: string;
    saveFormats?: string[];
    dataTypes?: Record<string, boolean>;
    compression?: boolean;
    chunkSize?: number;
    storageLocation?: string;
  }
  const isAWSCredentialsValid = () => false
  const realTimeDataSize = 0

  const [sessionName, setSessionName] = useState('')
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [sortBy, setSortBy] = useState('newest')
  const [showExportModal, setShowExportModal] = useState(false)
  const [selectedSessionForExport, setSelectedSessionForExport] = useState<SessionInfo | null>(null)
  
  // 🔧 저장 위치 선택 state
  
  // 🔧 레코딩 중지 및 업로드 상태 관리
  const [isStoppingRecording, setIsStoppingRecording] = useState(false)
  
  // 저장소 자동 복원 Hook 사용
  
  // 실시간 레코딩 시간 계산 (StatusBar와 동일한 로직)
  const [recordingDuration, setRecordingDuration] = useState<string>('00:00:00')
  
  // 🔧 실시간 데이터 크기 및 저장 위치 상태
  
  // AWS S3 연결 테스트 상태
  
  // 불완전한 세션 정리 상태
  
  // S3 연결 실패 상태 관리

  // Phase 4: 새로운 스토어 Hook 사용
  const { systemStatus, isInitialized } = useSystemStatus()
  const { isConnected, deviceName, currentDeviceId, batteryLevel } = useDeviceStatus()
  const { isStreaming } = useStreamingStatus()
  const { 
    initializeSystem,
    startRecording,
    stopRecording,
  } = useSystemActions()

  // 연결된 디바이스 상세 정보
  const connectedDevice = useConnectedDevice()
  const deviceMonitoring = useDeviceMonitoring()
  const samplingRates = useSamplingRates()

  // 🔧 실시간 배터리 정보 (DeviceManager와 동일한 방식)
  const [realtimeBatteryLevel, setRealtimeBatteryLevel] = useState<number>(0);
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isConnected && bluetoothService.isConnected()) {
      interval = setInterval(async () => {
        try {
          const batteryLevel = await bluetoothService.getBatteryLevel();
          setRealtimeBatteryLevel(batteryLevel);
        } catch (error) {
          logger.error('Failed to get realtime battery level:', error);
        }
      }, 1000); // 1초마다 업데이트 (DeviceManager와 동일)
    } else {
      setRealtimeBatteryLevel(0);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isConnected]);

  // SDD 010: IndexedDB 단일 저장소 — storageStore 간소화
  const { 
    isInitialized: storageInitialized,
    storageStats,
    currentSession,
    sessions, 
    loadSessions, 
    deleteSession, 
    deleteSessions, 
    selectSession, 
    selectAllSessions, 
    clearSelection, 
    selectedSessions,
  } = useStorageStore()

  // 설정 스토어에서 스트리밍 레코딩 형식과 타임존 설정 가져오기
  const { streamingRecordingFormat, setStreamingRecordingFormat, timezone } = useSettingsStore()

  // 🔧 분석 지표 서비스 인스턴스
  const analysisMetricsService = AnalysisMetricsService.getInstance()

  // 포맷팅 함수들 (StatusBar와 동일)
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  // AWS S3 연결 테스트 함수

  




  // 시스템 초기화
  useEffect(() => {
    if (!isInitialized && systemStatus === 'idle') {
      initializeSystem()
    }
  }, [isInitialized, systemStatus, initializeSystem])

  // IndexedDB 기반 저장 — 별도 초기화 불필요, 세션 목록 즉시 로드
  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  // 레코딩 상태 변경 시 세션 목록 새로고침
  useEffect(() => {
    if (!currentSession && storageInitialized) {
      logger.debug('🔧 레코딩 종료됨, 세션 목록 새로고침')
      // 레코딩이 종료되면 2초 후 세션 목록을 새로고침
      const timer = setTimeout(() => {
        loadSessions()
        
        // 세션 목록 새로고침 후 업로드 중인 세션들 정리
        setTimeout(() => {
          setUploadingSessions(new Set())
        }, 1000)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentSession, storageInitialized, loadSessions])

  // 실시간 레코딩 시간 계산 (StatusBar와 동일한 로직)
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentSession && currentSession.startTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const start = new Date(currentSession.startTime).getTime();
        const diff = Math.floor((now - start) / 1000);
        
        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);
        const seconds = diff % 60;
        
        setRecordingDuration(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      }, 1000);
    } else {
      setRecordingDuration('00:00:00');
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [currentSession?.startTime]);

  // 스트리밍 상태 실시간 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      // SystemControlService에서 실제 스트리밍 상태 확인하여 업데이트
      // 이는 useStreamingStatus가 실시간으로 반영되도록 도움
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // 자동 복원 오류 처리
  useEffect(() => {
    if (restoreError) {
      logger.error('[DATACENTER] ❌ 저장소 자동 복원 오류:', restoreError);
      // 오류가 발생해도 사용자에게는 조용히 처리하고 설정 모달만 표시
    }
  }, [restoreError]);

  // SDD 010: saveOptions 기본값은 IndexedDB 전용으로 고정 (settings 연동 제거됨)


  // 타임존 설정 변경 시 세션 목록 새로고침
  useEffect(() => {
    if (storageInitialized && timezone) {
      logger.debug('[DATACENTER] 🕐 타임존 설정 변경됨:', timezone, '세션 목록 새로고침');
      loadSessions();
    }
  }, [timezone, storageInitialized, loadSessions]);

  // 저장소 선택 핸들러 — SDD 010: IndexedDB 전용 (no-op)
  const handleSelectStorage = async () => {
    setShowStorageSetup(false);
  };

  const toggleRecording = async () => {
    if (currentSession) {
      // 스트리밍 레코딩 중지
      try {
        setIsStoppingRecording(true);
        
        // 현재 세션을 업로딩 목록에 추가
        setUploadingSessions(prev => new Set(prev).add(currentSession.id));
        
        logger.debug('🔧 레코딩 중지 시작:', currentSession.id);
        
        await stopRecording();
        
        logger.debug('✅ 레코딩 중지 완료:', currentSession.id);
        
        // 업로드 완료 후 상태 정리
        setUploadingSessions(prev => {
          const newSet = new Set(prev);
          newSet.delete(currentSession.id);
          return newSet;
        });
        
      } catch (error) {
        logger.error('Failed to stop streaming recording:', error);
        
        // 실패 시 업로딩 상태 해제
        if (currentSession) {
          setUploadingSessions(prev => {
            const newSet = new Set(prev);
            newSet.delete(currentSession.id);
            return newSet;
          });
        }
        
        alert(t.dataCenter.stopRecordingFailedAlert);
      } finally {
        setIsStoppingRecording(false);
      }
    } else {
      // 스트리밍 레코딩 시작 - 연결된 디바이스 필요
      if (!isConnected) {
        alert(t.dataCenter.connectDeviceFirstAlert)
        return
      }
      
      // 🔧 선택된 저장 위치에 따른 검증
      if (selectedStorageLocation === 'local' && !storageInitialized) {
        alert(t.dataCenter.selectStorageFirstAlert)
        return
      }
      
      if (selectedStorageLocation === 's3' && !isAWSCredentialsValid()) {
        alert(t.dataCenter.awsCredentialsAlert)
        return
      }
      
      try {
        // 선택된 형식을 StreamingSessionConfig에 맞게 변환
        const mappedFormat = streamingRecordingFormat === 'jsonl' ? 'json' : streamingRecordingFormat as ('json' | 'csv' | 'binary')
        
        // 실제 연결된 디바이스 정보 사용
        const actualDeviceName = connectedDevice?.device?.name || deviceName || 'Unknown Device'
        const actualDeviceId = connectedDevice?.device?.id || currentDeviceId || 'unknown'
        
        const config: StreamingSessionConfig = {
          sessionName: sessionName || `Session_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}`,
          deviceName: actualDeviceName,
          deviceId: actualDeviceId,
          saveFormats: [mappedFormat], // 사용자가 선택한 형식 사용
          dataTypes: {
            eegRaw: saveOptions.eegRaw,
            ppgRaw: saveOptions.ppgRaw,
            accRaw: saveOptions.accRaw,
            // 🔧 새로운 분석 지표 옵션 추가
            eegAnalysisMetrics: saveOptions.eegAnalysisMetrics,
            ppgAnalysisMetrics: saveOptions.ppgAnalysisMetrics,
            accAnalysisMetrics: saveOptions.accAnalysisMetrics
          },
          compression: false, // SDD 010: IndexedDB 전용
          chunkSize: 1024, // 1KB chunks
          // 🔧 저장 위치 정보 추가
          storageLocation: selectedStorageLocation
        }
        
        await startRecording(config.sessionName)
        setSessionName('') // 세션 이름 초기화
      } catch (error) {
        logger.error('Failed to start streaming recording:', error)
        alert(t.dataCenter.startRecordingFailedAlert)
      }
    }
  }

  // 필터 옵션
  const filterOptions = [
    { value: 'all', label: 'All Data' },
    { value: 'eeg', label: 'EEG Only' },
    { value: 'ppg', label: 'PPG Only' },
    { value: 'acc', label: 'ACC Only' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' }
  ]

  // 필터링된 데이터
  const filteredData = sessions.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.deviceName.toLowerCase().includes(searchQuery.toLowerCase())
    
    if (selectedFilter === 'all') return matchesSearch
    if (selectedFilter === 'today') {
      const today = new Date().toISOString().split('T')[0]
      return item.date === today && matchesSearch
    }
    if (selectedFilter === 'week') return matchesSearch // 임시로 모든 데이터 표시
    
    return item.formats.some((format: string) => format.toLowerCase() === selectedFilter) && matchesSearch
  }).sort((a, b) => {
    const timeA = a.startTime?.getTime?.() || 0;
    const timeB = b.startTime?.getTime?.() || 0;
    return sortBy === 'newest' ? timeB - timeA : timeA - timeB;
  })

  const getQualityBadge = (quality: number) => {
    if (quality >= 80) {
      return <Pill size="sm" variant="secondary" className="bg-green-500/10 text-green-400">{t.dataCenter.qualityExcellent}</Pill>
    } else if (quality >= 60) {
      return <Pill size="sm" variant="secondary" className="bg-indigo-500/10 text-indigo-400">{t.dataCenter.qualityGood}</Pill>
    } else if (quality >= 40) {
      return <Pill size="sm" variant="secondary" className="bg-yellow-500/10 text-yellow-400">{t.dataCenter.qualityFair}</Pill>
    } else {
      return <Pill size="sm" variant="secondary" className="bg-red-500/10 text-red-400">{t.dataCenter.qualityPoor}</Pill>
    }
  }

  // 세션 목록 수동 새로고침
  const handleRefreshSessions = async () => {
    logger.debug('🔧 세션 목록 수동 새로고침')
    await loadSessions()
  }

  // 저장소 변경 핸들러 — SDD 010: IndexedDB only, no-op
  const handleChangeStorage = async () => {
    await loadSessions();
  }

  // 세션 삭제 핸들러
  const handleDeleteSession = async (sessionId: string) => {
    // 업로드 중인 세션 또는 현재 레코딩 중인 세션은 삭제 불가
    if (uploadingSessions.has(sessionId) || (currentSession && currentSession.id === sessionId)) {
      toast.error(t.dataCenter.cannotDeleteActive, {
        description: t.dataCenter.cannotDeleteActiveDesc,
        duration: 3000,
      })
      return
    }
    
    try {
      const { detectOS } = await import('../utils/pathUtils');
      const isAndroid = detectOS() === 'android';
      
      logger.debug(`🗑️ 세션 삭제 시작: ${sessionId} (플랫폼: ${isAndroid ? '안드로이드' : '데스크톱'})`);
      
      if (isAndroid) {
        // 🔧 안드로이드 환경: SessionManager 기반 삭제 (localStorage만 사용)
        logger.debug('📱 안드로이드 환경: SessionManager 기반 삭제 수행');
        
        const { sessionManager } = await import('../utils/SessionManager');
        
        try {
          await sessionManager.deleteSession(sessionId);
          logger.debug('✅ 안드로이드 환경: SessionManager 삭제 완료');
          
          // 세션 목록 새로고침
          await loadSessions();
          
          toast.success(t.dataCenter.sessionDeleted);
        } catch (error) {
          logger.error('❌ 안드로이드 환경: SessionManager 삭제 실패:', error);
          
          // 🔧 안드로이드 환경: 대체 방법 - UI 레벨에서 삭제
          logger.debug('📱 안드로이드 환경: UI 레벨 삭제 시도');
          
          // 세션 목록 새로고침만 시도
          await loadSessions();
          
          toast.warning(t.dataCenter.androidDeleteLimited);
        }
      } else {
        // 🔧 데스크톱 환경: 기존 삭제 로직
        logger.debug('💻 데스크톱 환경: 기존 삭제 로직 수행');
        
        await deleteSession(sessionId);
        // 세션 목록 새로고침
        await loadSessions();
        
        toast.success(t.dataCenter.sessionDeleted);
      }
    } catch (error) {
      logger.error('❌ 세션 삭제 실패:', error);
      
      // 🔧 플랫폼 감지 실패 시 에러 메시지 개선
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (errorMessage.includes('File System Access API') || errorMessage.includes('showDirectoryPicker')) {
        toast.error(t.dataCenter.androidAccessLimited);
      } else {
        toast.error(t.dataCenter.sessionDeleteFailed);
      }
    }
  }

  // 세션 다운로드 핸들러
  const handleDownloadSession = async (sessionInfo: SessionInfo) => {
    // 업로드 중인 세션 또는 현재 레코딩 중인 세션은 다운로드 불가
    if (uploadingSessions.has(sessionInfo.id) || (currentSession && currentSession.id === sessionInfo.id)) {
      toast.error(t.dataCenter.cannotDownloadActive, {
        description: t.dataCenter.cannotDownloadActiveDesc,
        duration: 3000,
      })
      return
    }
    
    try {
      // SDD 010: all sessions stored in IndexedDB
      await handleExportSession(sessionInfo.id)
    } catch (error) {
      logger.error('세션 다운로드 실패:', error)
      toast(t.dataCenter.sessionDownloadFailed)
    }
  }

  // 세션 내보내기 핸들러
  const handleExportSession = async (sessionId: string) => {
    try {
      const session = sessions.find(s => s.id === sessionId)
      if (session) {
        setSelectedSessionForExport(session)
        setShowExportModal(true)
      }
    } catch (error) {
      logger.error('세션 내보내기 실패:', error)
    }
  }

  // 실제 내보내기 실행
  const handleExportExecute = async (sessionId: string, format: string, dirHandle?: FileSystemDirectoryHandle) => {
    try {
      logger.debug(`🎯 DataCenter.tsx handleExportExecute 호출됨 - sessionId: ${sessionId}, format: ${format}`);
      logger.debug(`🎯 Directory handle 전달됨:`, dirHandle ? `${dirHandle.name}` : 'undefined');
      
      if (format === 'zip' || format === 'all') {
        // ZIP 형태로 내보내기 - SessionManager 사용 (directory handle 전달)
        logger.debug(`🚀 DataCenter.tsx SessionManager.downloadAllAsZip 호출 시작`);
        const { sessionManager } = await import('../utils/SessionManager');
        const success = await sessionManager.downloadAllAsZip(sessionId, dirHandle);
        if (!success) {
          throw new Error('SessionManager ZIP 내보내기 실패');
        }
        logger.debug(`✅ DataCenter.tsx SessionManager.downloadAllAsZip 완료`);
      } else {
        // 기타 형식은 SessionManager 사용
        logger.debug(`🔧 DataCenter.tsx SessionManager.exportSession 호출 - format: ${format}`);
        const { sessionManager } = await import('../utils/SessionManager');
        await sessionManager.downloadCSV(sessionId, format as any);
      }
      
      // 내보내기 성공 후 모달 상태 정리는 onOpenChange에서 처리
      // 세션 목록 새로고침 (비동기로 백그라운드에서 실행)
      setTimeout(() => {
        loadSessions()
      }, 100)
    } catch (error) {
      logger.error('내보내기 실행 실패:', error)
      throw error // ExportSessionModal에서 오류 처리하도록 전달
    }
  }

  // 세션 경로 복사 (크로스 플랫폼 지원)
  const handleCopySessionPath = async (session: SessionInfo) => {
    // 업로드 중인 세션 또는 현재 레코딩 중인 세션은 경로 복사 불가
    if (uploadingSessions.has(session.id) || (currentSession && currentSession.id === session.id)) {
      toast.error(t.dataCenter.cannotCopyPathActive, {
        description: t.dataCenter.cannotCopyPathActiveDesc,
        duration: 3000,
      })
      return
    }
    
    try {
      // SDD 010: IndexedDB — no filesystem path
      const sessionPath = `IndexedDB → ${session.id}`;
      
      await navigator.clipboard.writeText(sessionPath);
      logger.debug('[DATACENTER] 📋 세션 경로 복사됨:', sessionPath);
      
      // 성공 토스트 메시지 표시
      toast.success(t.dataCenter.pathCopied, {
        description: `${session.name} ${t.dataCenter.pathCopiedDesc}`,
        duration: 3000,
      });
      
    } catch (error) {
      logger.error('[DATACENTER] ❌ 경로 복사 실패:', error);
      
      // 대안: 텍스트 선택 방식으로 복사 시도
      try {
        const absolutePath = "IndexedDB";
        
        // SDD 010: IndexedDB — session ID만 사용
        const sessionId = session.id;
        
        // 전체 저장소 구조 경로 생성
        const fullSessionPath = `IndexedDB > ${sessionId}`;
        
        const sessionPath = formatPathForSystem(fullSessionPath);
          
        const textArea = document.createElement('textarea');
        textArea.value = sessionPath;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        logger.debug('[DATACENTER] 📋 대안 방식으로 경로 복사됨:', sessionPath);
        
        // 성공 토스트 메시지 표시
        toast.success(t.dataCenter.pathCopied, {
          description: `${session.name} ${t.dataCenter.pathCopiedDesc}`,
          duration: 3000,
        });
        
      } catch (fallbackError) {
        logger.error('[DATACENTER] ❌ 대안 복사도 실패:', fallbackError);
        
        // 실패 토스트 메시지 표시
        toast.error(t.dataCenter.pathCopyFailed, {
          description: t.dataCenter.pathCopyFailedDesc,
          duration: 3000,
        });
      }
    }
  }

  // 플랫폼별 폴더 열기 안내 메시지 생성
  const getPlatformSpecificFolderInstructions = () => {
    const os = detectOS();
    // logger.debug('[DEBUG] Detected OS:', os); // 디버깅용 로그
    // logger.debug('[DEBUG] User Agent:', navigator.userAgent); // 디버깅용 로그
    // logger.debug('[DEBUG] Platform:', navigator.platform); // 디버깅용 로그
    
    switch (os) {
      case 'windows':
        return {
          title: t.dataCenter.folderWinTitle,
          description: t.dataCenter.folderWinDesc
        };
      case 'macos':
        return {
          title: t.dataCenter.folderMacTitle,
          description: t.dataCenter.folderMacDesc
        };
      case 'linux':
        return {
          title: t.dataCenter.folderLinuxTitle,
          description: t.dataCenter.folderLinuxDesc
        };
      default:
        return {
          title: t.dataCenter.folderGenericTitle,
          description: t.dataCenter.folderGenericDesc
        };
    }
  };

  return (
    <PageShell>
        {/* Header */}
        <PageHeader title={t.dataCenter.title} subtitle={t.dataCenter.storageSubtitle}>
          <Pill 
            variant="secondary" 
            onClick={handleChangeStorage}
          >
            <Settings strokeWidth={1.5} className="w-4 h-4 mr-2" />
            {t.dataCenter.storageSettingsBtn}
          </Pill>
        </PageHeader>

        {/* Storage Directory Status */}
        {!storageInitialized && (
          <Card className="p-4 border-yellow-200 bg-yellow-500/10">
            <div className="flex items-center gap-3">
              <AlertCircle strokeWidth={1.5} className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-400">{t.dataCenter.selectStorageDirectory}</p>
                <p className="text-xs text-yellow-400">{t.dataCenter.selectStorageDirectoryDesc}</p>
              </div>
              <Pill 
                size="sm" 
                onClick={handleSelectStorage}
                className="ml-auto"
              >
                <FolderOpen strokeWidth={1.5} className="w-4 h-4 mr-2" />
                {t.dataCenter.selectAction}
              </Pill>
            </div>
          </Card>
        )}

        {storageInitialized && (
          <Card className="p-4 border-green-200 bg-green-500/10">
            <div className="flex items-center gap-3">
              <CheckCircle strokeWidth={1.5} className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-400">{t.dataCenter.storageConnected}</p>
                <p className="text-xs text-green-700 font-mono">
                  {formatPathForSystem("IndexedDB")}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Storage Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="lb-eyebrow">Total Storage</div>
                <div className="text-2xl font-medium t-strong mt-2">{storageStats.totalSize}</div>
              </div>
              <HardDrive strokeWidth={1.5} className="w-6 h-6 text-indigo-400" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="lb-eyebrow">Used</div>
                <div className="text-2xl font-medium t-strong mt-2">{storageStats.used}</div>
              </div>
              <Database strokeWidth={1.5} className="w-6 h-6 text-green-500" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="lb-eyebrow">Available</div>
                <div className="text-2xl font-medium t-strong mt-2">{storageStats.available}</div>
              </div>
              <HardDrive strokeWidth={1.5} className="w-6 h-6 text-white/40" />
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="lb-eyebrow">Sessions</div>
                <div className="text-2xl font-medium t-strong mt-2">{storageStats.sessions}</div>
              </div>
              <Database strokeWidth={1.5} className="w-6 h-6 text-orange-500" />
            </div>
          </Card>
        </div>

        {/* Recording Control */}
        <div className="space-y-4">
          <h3 className="font-medium t-strong">{t.dataCenter.realtimeStreamingRecording}</h3>
          
          {isConnected ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* 디바이스 정보 카드 */}
              <Card className="p-4 bg-card dark:bg-background border shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                    <Smartphone strokeWidth={1.5} className="w-5 h-5 text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium t-strong">{t.dataCenter.deviceInfo}</h3>
                    <p className="text-sm t-soft">{t.dataCenter.connectedLinkBand}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm t-soft">{t.dataCenter.deviceNameLabel}</span>
                    <span className="text-sm font-medium">{connectedDevice?.device?.name || deviceName || 'Unknown Device'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm t-soft">{t.dataCenter.deviceIdLabel}</span>
                    <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{connectedDevice?.device?.id || currentDeviceId || 'Unknown'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm t-soft">{t.dataCenter.connectionStatus}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500/10"></div>
                      <span className="text-sm font-medium text-green-600">{t.dataCenter.connected}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm t-soft">{t.dataCenter.batteryLabel}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{realtimeBatteryLevel}%</span>
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        realtimeBatteryLevel > 20 ? "bg-green-500/10" : realtimeBatteryLevel > 10 ? "bg-yellow-500/10" : "bg-red-500"
                      )}></div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t.dataCenter.connectionTime}</span>
                    <span className="text-sm font-medium">{deviceMonitoring?.connectionDuration || '00:00:00'}</span>
                  </div>
                  
                  {/* 샘플링 레이트 */}
                  {samplingRates && (
                    <div className="pt-3 border-t">
                      <p className="text-sm text-muted-foreground mb-2">{t.dataCenter.samplingRateLabel}</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="text-center p-2 bg-muted/50 rounded">
                          <div className="font-medium">EEG</div>
                          <div className="text-muted-foreground">{samplingRates.eeg}Hz</div>
                        </div>
                        <div className="text-center p-2 bg-muted/50 rounded">
                          <div className="font-medium">PPG</div>
                          <div className="text-muted-foreground">{samplingRates.ppg}Hz</div>
                        </div>
                        <div className="text-center p-2 bg-muted/50 rounded">
                          <div className="font-medium">ACC</div>
                          <div className="text-muted-foreground">{samplingRates.acc}Hz</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* 저장소 정보 카드 */}
              {storageInitialized && (
                <Card className="p-4 bg-card dark:bg-background border shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <FolderOpen strokeWidth={1.5} className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-medium t-strong">{t.dataCenter.storageInfoCard}</h3>
                      <p className="text-sm t-soft">{t.dataCenter.storageLocationLabel}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm t-soft">{t.dataCenter.storagePath}</span>
                      <div className="mt-1 p-3 bg-muted/50 rounded-lg">
                        <p className="font-mono text-xs break-all">{formatPathForSystem("IndexedDB")}</p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Pill
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          try {
                            const formattedPath = formatPathForSystem("IndexedDB");
                            await navigator.clipboard.writeText(formattedPath);
                            
                            toast.success(t.dataCenter.storagePathCopied, {
                              description: t.dataCenter.storagePathCopiedDesc,
                              duration: 3000,
                            });
                            
                          } catch (error) {
                            logger.error('클립보드 복사 실패:', error);
                            
                            try {
                              const formattedPath = formatPathForSystem("IndexedDB");
                              const textArea = document.createElement('textarea');
                              textArea.value = formattedPath;
                              document.body.appendChild(textArea);
                              textArea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textArea);
                              
                              toast.success(t.dataCenter.storagePathCopied, {
                                description: t.dataCenter.storagePathCopiedDesc,
                                duration: 3000,
                              });
                              
                            } catch (fallbackError) {
                              toast.error(t.dataCenter.pathCopyFailed, {
                                description: t.dataCenter.pathCopyFailedDesc,
                                duration: 3000,
                              });
                            }
                          }
                        }}
                        className="flex-1"
                      >
                        <Copy strokeWidth={1.5} className="w-4 h-4 mr-1" />
                        {t.dataCenter.copyPath}
                      </Pill>
                      <Pill
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          try {
                            const formattedPath = formatPathForSystem("IndexedDB");
                            await navigator.clipboard.writeText(formattedPath);
                            
                            const instructions = getPlatformSpecificFolderInstructions();
                            
                            toast.info(t.dataCenter.storageFolderPathCopied, {
                              description: instructions.description,
                              duration: 5000,
                            });
                            
                          } catch (error) {
                            logger.error('디렉토리 경로 복사 실패:', error);
                            
                            try {
                              const formattedPath = formatPathForSystem("IndexedDB");
                              const textArea = document.createElement('textarea');
                              textArea.value = formattedPath;
                              document.body.appendChild(textArea);
                              textArea.select();
                              document.execCommand('copy');
                              document.body.removeChild(textArea);
                              
                              const instructions = getPlatformSpecificFolderInstructions();
                              
                              toast.info(t.dataCenter.storageFolderPathCopied, {
                                description: instructions.description,
                                duration: 5000,
                              });
                              
                            } catch (fallbackError) {
                              toast.error(t.dataCenter.folderOpenFailed, {
                                description: t.dataCenter.folderOpenFailedDesc,
                                duration: 3000,
                              });
                            }
                          }
                        }}
                        className="flex-1"
                        title={getPlatformSpecificFolderInstructions().title}
                      >
                        <FolderOpen strokeWidth={1.5} className="w-4 h-4 mr-1" />
                        {t.dataCenter.openFolder}
                      </Pill>
                    </div>
                  </div>
                </Card>
              )}


            </div>
          ) : (
            <div className="space-y-4">

              
              {/* 디바이스 연결 경고 */}
              <div className="border-red-600 bg-red-900/20 border-l-4 border-l-red-600 w-full rounded-lg">
                <div className="flex flex-col items-center justify-center text-center px-8 py-8">
                  <AlertCircle strokeWidth={1.5} className="h-12 w-12 text-red-400 mb-6" />
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-red-200">{t.dataCenter.connectDeviceTitle}</h3>
                    <p className="text-lg text-red-300">
                      {t.dataCenter.connectDeviceDesc}
                    </p>
                    <p className="text-lg text-red-200/80">
                      {t.dataCenter.connectDeviceTip}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 레코딩 제어 카드 */}
          <Card className="p-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div className="space-y-2">
                <h3 className="font-medium t-strong">{t.dataCenter.recordingControl}</h3>
                
                {/* 레코딩 상태 표시 */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      isConnected ? "bg-green-500/10" : "bg-muted"
                    )}></div>
                    <span className={isConnected ? "text-green-600" : "text-muted-foreground"}>
                      {isConnected ? t.dataCenter.deviceConnected : t.dataCenter.deviceNotConnected}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      storageInitialized ? "bg-green-500/10" : "bg-muted"
                    )}></div>
                    <span className={storageInitialized ? "text-green-600" : "text-muted-foreground"}>
                      {storageInitialized ? t.dataCenter.storageReady : t.dataCenter.storageSetupNeeded}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      isStreaming ? "bg-indigo-500 animate-pulse" : "bg-muted"
                    )}></div>
                    <span className={isStreaming ? "text-indigo-400" : "text-muted-foreground"}>
                      {isStreaming ? t.dataCenter.dataStreaming : t.dataCenter.streamingStopped}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      false ? "bg-red-500 animate-pulse" : "bg-muted"
                    )}></div>
                    <div className="flex flex-col">
                      <span className={false ? "text-red-600" : "text-muted-foreground"}>
                        {false ? t.dataCenter.fileSaving : t.dataCenter.saveWaiting}
                      </span>
                      {/* 저장 위치 — SDD 010: IndexedDB 전용 */}
                      <span className="text-xs text-muted-foreground">
                        💾 IndexedDB
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                {/* 세션 이름 입력 */}
                {!currentSession && (
                  <Input
                    placeholder={t.dataCenter.sessionNamePlaceholder}
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    className="w-48"
                  />
                )}
                
                {currentSession && (
                  <div className="flex flex-col items-end gap-1 min-w-[140px]">
                    <p className="text-sm font-medium text-white/70 truncate max-w-[200px]">{currentSession.name}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                      <span className="text-2xl font-bold text-white tabular-nums tracking-tight">
                        {recordingDuration}
                      </span>
                    </div>
                    <p className="text-[11px] text-red-400/60 font-medium uppercase tracking-wider">Recording</p>
                  </div>
                )}
                
                <Pill 
                  onClick={toggleRecording}
                  disabled={!isConnected || !storageInitialized || isStoppingRecording}
                  className={cn(
                    currentSession 
                      ? "bg-red-500 hover:bg-red-600 text-white" 
                      : "bg-green-500/10 hover:bg-green-600 text-white"
                  )}
                >
                  {currentSession ? (
                    <>
                      {isStoppingRecording ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>{t.dataCenter.savingProgress}</span>
                        </div>
                      ) : (
                        <>
                          <Square strokeWidth={1.5} className="w-4 h-4 mr-2" />
                          {t.dataCenter.stopRecordingShort}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Play strokeWidth={1.5} className="w-4 h-4 mr-2" />
                      {t.dataCenter.startRecordingShort}
                    </>
                  )}
                </Pill>
              </div>
            </div>

            {/* 저장 형식 설정 */}
            {!currentSession && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="lb-eyebrow mb-3">{t.dataCenter.saveSettings}</h4>
                
                {/* 저장 위치 — SDD 010: IndexedDB 전용 */}
                <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Database strokeWidth={1.5} className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-medium">{t.dataCenter.indexedDbInfo}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t.dataCenter.indexedDbDesc}
                  </p>
                </div>
                
                {/* 데이터 타입 선택 */}
                <div>
                  <label className="lb-eyebrow block mb-2">{t.dataCenter.dataTypesToSave}</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(saveOptions).map(([key, value]) => (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => {
                            const newSaveOptions = {
                              ...saveOptions,
                              [key]: e.target.checked
                            };
                            setSaveOptions(newSaveOptions);
                            
                            // SDD 010: config removed — saveOptions는 state로 관리
                            logger.debug('[DATACENTER] 🔧 저장 옵션 변경됨:', newSaveOptions);
                          }}
                          className="rounded border-input text-primary focus:ring-primary"
                        />
                        <span>
                          {(() => {
                            const formatted = key.replace(/([A-Z])/g, ' $1').trim();
                            return formatted
                              .replace(/^eeg/i, 'EEG')
                              .replace(/^ppg/i, 'PPG') 
                              .replace(/^acc/i, 'ACC')
                              .replace(/\beeg\b/gi, 'EEG')
                              .replace(/\bppg\b/gi, 'PPG')
                              .replace(/\bacc\b/gi, 'ACC');
                          })()}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:min-h-[56px]">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search strokeWidth={1.5} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t.dataCenter.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select 
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-3 py-2 border border-input bg-card text-foreground rounded-md text-sm"
              >
                {filterOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Pill variant="secondary" size="sm">
                <Filter strokeWidth={1.5} className="w-4 h-4 mr-2" />
                {t.dataCenter.advancedFilter}
              </Pill>
            </div>
          </div>
        </Card>

        {/* Data Table */}
        <Card className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h3 className="font-medium t-strong">{t.dataCenter.savedSessions}</h3>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Pill
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                  >
                    <ArrowUpDown strokeWidth={1.5} className="h-4 w-4" />
                    {sortBy === 'newest' ? t.dataCenter.newest : t.dataCenter.oldest}
                  </Pill>
                  {showSortDropdown && (
                    <div className="absolute top-full right-0 mt-1 w-40 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 py-1">
                      <button
                        onClick={() => { setSortBy('newest'); setShowSortDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${sortBy === 'newest' ? 'text-indigo-400 bg-indigo-500/10' : 'text-white hover:bg-white/[0.05]'}`}
                      >
                        {t.dataCenter.newest}
                      </button>
                      <button
                        onClick={() => { setSortBy('oldest'); setShowSortDropdown(false); }}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${sortBy === 'oldest' ? 'text-indigo-400 bg-indigo-500/10' : 'text-white hover:bg-white/[0.05]'}`}
                      >
                        {t.dataCenter.oldest}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[140px]">{t.dataCenter.colSessionName}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[160px]">{t.dataCenter.colDateTime}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[100px]">{t.dataCenter.colDuration}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[120px]">{t.dataCenter.colDevice}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[120px]">{t.dataCenter.colDataTypes}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[80px]">{t.dataCenter.colSize}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[100px]">{t.dataCenter.colLocation}</th>
                  <th className="lb-eyebrow-xs text-left py-3 px-4 min-w-[80px]">{t.dataCenter.colActions}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id} className="border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <FileText strokeWidth={1.5} className="w-4 h-4 text-muted-foreground" />
                        <span className="t-strong font-medium">{item.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm t-soft">
                        <Calendar strokeWidth={1.5} className="w-4 h-4 text-muted-foreground" />
                        <span>{item.date}</span>
                        <Clock strokeWidth={1.5} className="w-4 h-4 text-muted-foreground ml-2" />
                        <span>{item.time}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm t-soft">{item.duration}</td>
                    <td className="py-3 px-4 text-sm t-soft">{item.deviceName}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {item.formats.map((format: string) => (
                          <Pill key={format} size="sm" variant="secondary" className="text-xs">
                            {format}
                          </Pill>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm t-soft">{item.totalSize}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Database strokeWidth={1.5} className="w-4 h-4 text-indigo-400" />
                          <span className="text-sm text-indigo-400">IndexedDB</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {/* 업로드 중인 세션 또는 현재 레코딩 중인 세션 표시 */}
                        {uploadingSessions.has(item.id) || (currentSession && currentSession.id === item.id) ? (
                          <div className="flex items-center gap-2 text-indigo-400">
                            <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-blue-600 rounded-full animate-spin"></div>
                            <span className="text-sm">
                              {currentSession && currentSession.id === item.id ? t.dataCenter.recordingInProgress : t.dataCenter.uploading}
                            </span>
                          </div>
                        ) : (
                          <>
                            {/* 경로 복사 버튼은 로컬 파일에만 표시 */}
                            {true && (
                              <Pill 
                                variant="secondary" 
                                size="sm"
                                onClick={() => handleCopySessionPath(item)}
                                title={t.dataCenter.copyAbsolutePath}
                              >
                                <Copy strokeWidth={1.5} className="w-4 h-4" />
                              </Pill>
                            )}
                            {/* 내려받기 버튼은 모든 세션에 표시 */}
                            <Pill 
                              variant="secondary" 
                              size="sm"
                              onClick={() => handleDownloadSession(item)}
                              title={t.dataCenter.downloadSession}
                            >
                              <Download strokeWidth={1.5} className="w-4 h-4" />
                            </Pill>
                            {/* 삭제 버튼은 모든 세션에 표시 */}
                            <Pill 
                              variant="secondary" 
                              size="sm"
                              onClick={() => handleDeleteSession(item.id)}
                              className="text-red-600 hover:text-red-700"
                              title={t.dataCenter.deleteSession}
                            >
                              <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                            </Pill>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 모바일 세션 카드 — 한 화면에 전체 정보 표시 (데스크톱 테이블 대체) */}
          <div className="md:hidden space-y-3">
            {filteredData.map((item) => (
              <div key={item.id} className="border border-white/[0.06] rounded-lg p-4 bg-white/[0.02]">
                {/* 제목 (한 줄) */}
                <div className="flex items-start gap-2 min-w-0 mb-1.5">
                  <FileText strokeWidth={1.5} className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                  <span className="t-strong font-medium break-all">{item.name}</span>
                </div>

                {/* 날짜 (한 줄) */}
                <div className="flex items-center gap-1.5 text-sm t-soft mb-3">
                  <Calendar strokeWidth={1.5} className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="whitespace-nowrap">{item.date} {item.time}</span>
                </div>

                {/* 기간 · 디바이스 · 저장 위치 · 크기 */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm t-soft mb-3">
                  <span className="flex items-center gap-1.5">
                    <Clock strokeWidth={1.5} className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    {item.duration}
                  </span>
                  <span className="max-w-[140px] truncate">{item.deviceName}</span>
                  <span className="flex items-center gap-1.5">
                    <Database strokeWidth={1.5} className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                    <span className="text-indigo-400">IndexedDB</span>
                    <span>· {item.totalSize}</span>
                  </span>
                </div>

                {/* 포맷 + 작업 버튼 */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-wrap gap-1">
                    {item.formats.map((format: string) => (
                      <Pill key={format} size="sm" variant="secondary" className="text-xs">
                        {format}
                      </Pill>
                    ))}
                  </div>
                  {uploadingSessions.has(item.id) || (currentSession && currentSession.id === item.id) ? (
                    <div className="flex items-center gap-2 text-indigo-400 shrink-0">
                      <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-blue-600 rounded-full animate-spin"></div>
                      <span className="text-sm">
                        {currentSession && currentSession.id === item.id ? t.dataCenter.recordingInProgress : t.dataCenter.uploading}
                      </span>
                    </div>
                  ) : (
                    <div className="flex gap-1 shrink-0">
                      <Pill variant="secondary" size="sm" onClick={() => handleCopySessionPath(item)} title={t.dataCenter.copyAbsolutePath}>
                        <Copy strokeWidth={1.5} className="w-4 h-4" />
                      </Pill>
                      <Pill variant="secondary" size="sm" onClick={() => handleDownloadSession(item)} title={t.dataCenter.downloadSession}>
                        <Download strokeWidth={1.5} className="w-4 h-4" />
                      </Pill>
                      <Pill variant="secondary" size="sm" onClick={() => handleDeleteSession(item.id)} className="text-red-600 hover:text-red-700" title={t.dataCenter.deleteSession}>
                        <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                      </Pill>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {restoreError && !isRestoring && (
            <ErrorState
              title={t.dataCenter.storageRestoreError}
              message={typeof restoreError === 'string' ? restoreError : t.dataCenter.storageRestoreErrorDesc}
            />
          )}
          {isRestoring && filteredData.length === 0 && (
            <LoadingState message={t.dataCenter.loadingSessions} />
          )}
          {!isRestoring && filteredData.length === 0 && (
            <EmptyState
              icon={<Database strokeWidth={1.5} className="w-8 h-8 text-muted-foreground" />}
              title={t.dataCenter.noSessionsFound}
              description={t.dataCenter.noSessionsFoundDesc}
            />
          )}
        </Card>
      
      {/* Storage Setup Modal */}
      {/* StorageSetupModal — SDD 010 제거됨 */}

      {/* Export Session Modal */}
      <ExportSessionModal
        open={showExportModal}
        onOpenChange={(open) => {
          setShowExportModal(open);
          if (!open) {
            // 모달이 닫힐 때 선택된 세션 정리
            setTimeout(() => {
              setSelectedSessionForExport(null);
            }, 200);
          }
        }}
        session={selectedSessionForExport}
        onExport={handleExportExecute}
      />
      
      {/* S3 Download Modal */}
      {/* S3 Download Modal — SDD 010: S3 제거됨 */}
  </PageShell>
  )
}